/**
 * Wires `collection.model.ts` to the reads and writes behind the collections
 * screen.
 *
 * Calls `fetchCollections`, then `fetchCollectionWorks` once per collection —
 * an N+1 kept on purpose, because the alternative that is NOT allowed is a
 * client-side join over a wider fetch; the fix when it matters is a batched
 * endpoint behind `ondo.works_in()`. Writes are `createCollection` and
 * `shareCollection`, each followed by a reload rather than a local patch.
 *
 * The state the model cannot hold is the selected collection and status, the
 * busy flag and action error, the reload counter, and the works keyed by the
 * signature of the collection list that asked for them — so a late answer for
 * a previous list can never render against the new one, and "partial" is
 * derived from that mismatch rather than stored.
 */

import type { AccessLevel, Capabilities, CollectionSummary } from "@ondo/shared";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import {
  createCollection,
  fetchCollectionWorks,
  fetchCollections,
  shareCollection,
} from "../lib/api.ts";
import { useAccount } from "../lib/account.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { entityNamesOf } from "../lib/works.ts";
import type { EntityNames } from "../lib/works.ts";
import { FOCUS_PARAMS } from "../routes/routes.constants.ts";
import { ALL_COLLECTIONS, COLLECTION_COPY } from "./collection.constants.ts";
import type { StatusFilterKey } from "./collection.constants.ts";
import {
  collectionChoices,
  describeError,
  offeredFilters,
  selectCards,
  selectedCollectionName,
  toCollectionRow,
  ownerChoices,
  validateCollectionName,
  validateOndoId,
} from "./collection.model.ts";
import type {
  CollectionGroup,
  CollectionRow,
  OwnerChoice,
  PlacedWork,
  StatusFilter,
  WorkCard,
} from "./collection.model.ts";

export type CollectionView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  /** No collections at all. Needs a create form, not a filter row. */
  | {
      kind: "empty";
      message: string;
      canCreate: boolean;
      /**
       * The two fields this branch went without.
       *
       * Every other branch carries them, and their absence here was not a
       * simplification: this is the ONLY screen a brand-new collector sees, so
       * it was the one place where a refused name produced no sentence and a
       * double-press produced two collections.
       */
      busy: boolean;
      error: string | null;
    }
  | {
      kind: "grid";
      cards: WorkCard[];
      /** "12 artworks registered" — already pluralised. */
      summary: string;
      collections: CollectionRow[];
      filters: StatusFilter[];
      activeFilter: StatusFilterKey;
      collectionGroups: CollectionGroup[];
      activeCollection: string;
      /** The heading over the grid: one collection's name, or all of them. */
      activeCollectionName: string;
      /** Some collections' works are still arriving; the grid shows what it has. */
      partial: boolean;
      /** The New collection form is open. Read from the URL; see `FOCUS_PARAMS.create`. */
      creating: boolean;
      busy: boolean;
      error: string | null;
    }
  /** Filters that match nothing. A different sentence from an empty collection. */
  | {
      kind: "no-matches";
      message: string;
      collections: CollectionRow[];
      filters: StatusFilter[];
      activeFilter: StatusFilterKey;
      collectionGroups: CollectionGroup[];
      activeCollection: string;
      activeCollectionName: string;
      creating: boolean;
      busy: boolean;
      error: string | null;
    };

export interface CollectionController {
  view: CollectionView;
  chooseCollection: (key: string) => void;
  chooseFilter: (key: StatusFilterKey) => void;
  /**
   * Who a new collection could belong to — OND-184. Empty when there is nothing
   * to choose, and the form then draws no picker.
   *
   * A sibling of `view` rather than a field on it, like `chosen` on the offers
   * controller: it is the same answer on all three arms of the union and
   * carrying it three times would be three places to forget.
   */
  owners: OwnerChoice[];
  /** `ondoId` null creates it for the caller, which is every collection before OND-184. */
  addCollection: (name: string, ondoId: string | null) => void;
  /** Close the New collection form: drops the parameter the header's link set. */
  closeCreate: () => void;
  share: (input: { collectionId: string; ondoId: string; level: AccessLevel }) => void;
  reload: () => void;
}

/**
 * The collector's works, across every collection they can see.
 *
 * Two loads, separate on purpose: `GET /collections` answers who holds what,
 * and one `GET /collections/:id/works` per collection answers what is in it.
 * Only the second carries a location, because only the collection door
 * discloses one.
 *
 * The fan-out is N+1 and knowingly so at early collection counts. The fix when
 * it matters is a batched works endpoint behind `ondo.works_in()`, which the
 * policies already make safe. It must not become a client-side join over a
 * wider fetch — that would put the filtering back in the browser, which is the
 * drift the whole authorization design exists to prevent.
 */
export function useCollectionController(
  capabilities: Capabilities,
  workBase: string,
): CollectionController {
  // OND-184. Read from the one copy of the account rather than threaded down
  // from the workspace: `useAccount` is where every screen that needs the
  // caller gets them, and a second `/me` on this screen would be a second
  // answer to a question already answered.
  const { me: meState } = useAccount();
  const owners = ownerChoices(meState.status === "ready" ? meState.me : null);
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [loaded, setLoaded] = useState<{ signature: string; placed: PlacedWork[] } | null>(
    null,
  );
  const [failure, setFailure] = useState<string | null>(null);
  const images = useWorkThumbnails(loaded ? loaded.placed : null, ({ work }) => work);
  const [selected, setSelected] = useState<string>(ALL_COLLECTIONS);
  const [status, setStatus] = useState<StatusFilterKey>("all");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // The form's open state is the URL's, not this hook's: the header's "New
  // collection" is a link to this screen carrying `?new=1`, because the shell
  // is a frame and holds nothing of the screen. A pasted link with the
  // parameter and no capability opens nothing — the server's answer gates
  // the form as it gates the button.
  const [params, setParams] = useSearchParams();
  const creating = capabilities.createCollection && params.has(FOCUS_PARAMS.create);
  const closeCreate = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(FOCUS_PARAMS.create);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let active = true;

    fetchCollections()
      .then((rows) => {
        if (active) setCollections(rows);
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, [nonce]);

  const signature = collections
    ? collections
        .map((collection) => collection.id)
        .sort()
        .join(",")
    : null;

  useEffect(() => {
    if (!collections || signature === null) return;

    let active = true;

    Promise.all(
      collections.map((collection) =>
        fetchCollectionWorks(collection.id).then((works) =>
          works.map((work) => ({ work, collectionId: collection.id })),
        ),
      ),
    )
      .then((batches) => {
        if (active) setLoaded({ signature, placed: batches.flat() });
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, [collections, signature]);

  const run = (action: Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    action
      .then(() => reload())
      .catch((error: unknown) => setActionError(describeError(error)))
      .finally(() => setBusy(false));
  };

  return {
    view: resolve({
      collections,
      loaded,
      signature,
      failure,
      images,
      selected,
      status,
      busy,
      actionError,
      creating,
      capabilities,
      workBase,
      // OND-52. Resolved from the one copy of the account that is already read
      // here for `ownerChoices`, so a grid spanning a person and their
      // companies names the holder of each card rather than calling them all
      // "Yours". Nothing is fetched and no id in it is one this reader could
      // not already name.
      entities: entityNamesOf(meState.status === "ready" ? meState.me : null),
    }),
    chooseCollection: setSelected,
    chooseFilter: setStatus,
    owners,
    addCollection: (name, ondoId) => {
      const problem = validateCollectionName(name);
      if (problem) {
        setActionError(problem);
        return;
      }
      // Closed on the way in rather than on success: the form was asked for
      // and answered, and a refusal is shown by `error` beside the grid.
      closeCreate();
      // OND-184. `ondoId` goes through unexamined: whether it is a firm the
      // caller administers is answered inside `ondo.create_collection_as()`,
      // which gives one refusal covering an unknown id and an id they do not
      // administer so the id space stays unwalkable.
      run(createCollection({ name: name.trim(), ondoId }));
    },
    closeCreate,
    share: ({ collectionId, ondoId, level }) => {
      const problem = validateOndoId(ondoId);
      if (problem) {
        setActionError(problem);
        return;
      }
      run(shareCollection(collectionId, { ondoId: ondoId.trim().toUpperCase(), level }));
    },
    reload,
  };
}

function resolve(input: {
  collections: CollectionSummary[] | null;
  loaded: { signature: string; placed: PlacedWork[] } | null;
  signature: string | null;
  failure: string | null;
  /** Signed photograph URLs by work id. Empty until they arrive. */
  images: Map<string, string>;
  selected: string;
  status: StatusFilterKey;
  busy: boolean;
  actionError: string | null;
  creating: boolean;
  capabilities: Capabilities;
  workBase: string;
  /** The entities the reader acts for, so a card can name the one holding it — OND-52. */
  entities: EntityNames;
}): CollectionView {
  if (input.failure) return { kind: "error", message: input.failure };
  if (!input.collections) return { kind: "loading" };

  if (input.collections.length === 0) {
    return {
      kind: "empty",
      message: COLLECTION_COPY.noCollections,
      canCreate: input.capabilities.createCollection,
      busy: input.busy,
      error: input.actionError,
    };
  }

  const rows = input.collections.map(toCollectionRow);
  const collectionGroups = collectionChoices(input.collections);
  const filters = offeredFilters();
  // Derived rather than stored: the works belong to the collection list that
  // asked for them, so a response for a previous list can never be rendered
  // against a new one.
  const partial = input.loaded?.signature !== input.signature;
  const fresh = partial ? [] : (input.loaded?.placed ?? []);

  const cards = selectCards({
    workBase: input.workBase,
    placed: fresh,
    collections: input.collections,
    selected: input.selected,
    status: input.status,
    images: input.images,
    entities: input.entities,
  });

  const filtering = input.status !== "all" || input.selected !== ALL_COLLECTIONS;
  if (cards.length === 0 && !partial && filtering) {
    return {
      kind: "no-matches",
      message: COLLECTION_COPY.noMatches,
      collections: rows,
      filters,
      activeFilter: input.status,
      collectionGroups,
      activeCollection: input.selected,
      activeCollectionName: selectedCollectionName(input.collections, input.selected),
      creating: input.creating,
      busy: input.busy,
      error: input.actionError,
    };
  }

  return {
    kind: "grid",
    cards,
    summary: summarise(cards.length),
    collections: rows,
    filters,
    activeFilter: input.status,
    collectionGroups,
    activeCollection: input.selected,
    activeCollectionName: selectedCollectionName(input.collections, input.selected),
    partial,
    creating: input.creating,
    busy: input.busy,
    error: input.actionError,
  };
}

function summarise(count: number): string {
  if (count === 0) return "Nothing registered yet";
  return count === 1 ? "1 artwork registered" : `${count} artworks registered`;
}
